import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'convex/react'
import { ArrowLeft, Download, Pencil } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'
import { formatCurrency, formatDate } from '@/lib/utils'
import { downloadExternalPrescriptionPdf } from '@/lib/externalPrescriptionPdf'
import { ExternalPrescriptionForm, externalPrescriptionError } from './ExternalPrescriptionForm'
import { ExternalPrescriptionStatusBadge, externalPrescriptionStatuses, type ExternalPrescriptionStatus } from './ExternalPrescriptionList'

export function ExternalPrescriptionDetails() {
  const { id } = useParams<{ id: string }>()
  const { profile } = useAuth()
  return <ExternalPrescriptionDetailsContent key={`${profile?._id ?? ''}:${profile?.company_id ?? ''}:${id ?? ''}`} id={id} />
}

function ExternalPrescriptionDetailsContent({ id }: { id?: string }) {
  const { profile } = useAuth()
  const { hasPermission, isLoading: permissionsLoading } = usePermissions()
  const canView = hasPermission('view_external_prescriptions')
  const canQuery = Boolean(id && canView && profile?.company_id)
  const canEdit = hasPermission('create_external_prescription')
  const canViewPrices = hasPermission('view_external_prices')
  const prescription = useQuery(api.externalLabs.getPrescription, canQuery ? { id: id as Id<'external_prescriptions'> } : 'skip')
  const setPrescriptionStatus = useMutation(api.externalLabs.setPrescriptionStatus)
  const [editing, setEditing] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [status, setStatus] = useState<ExternalPrescriptionStatus>('creata')
  const [savingStatus, setSavingStatus] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const savedStatus = prescription?.status

  useEffect(() => {
    if (savedStatus) setStatus(savedStatus)
  }, [savedStatus])
  useEffect(() => { setEditing(false); setError(null); setNotice(null) }, [id])

  const changeStatus = async () => {
    if (!prescription || savingStatus) return
    setSavingStatus(true)
    setError(null)
    setNotice(null)
    try {
      await setPrescriptionStatus({ id: prescription._id, status })
      setNotice('Stato aggiornato.')
    } catch (cause) {
      setError(externalPrescriptionError(cause, 'Stato non aggiornato. Riprova.'))
    } finally {
      setSavingStatus(false)
    }
  }
  const download = async () => {
    if (!prescription || downloading) return
    setDownloading(true)
    setError(null)
    try {
      await downloadExternalPrescriptionPdf(prescription)
    } catch (cause) {
      setError(cause instanceof Error && cause.message.includes('non è supportato nel PDF') ? cause.message : 'Non è stato possibile creare il PDF. Riprova.')
    } finally {
      setDownloading(false)
    }
  }

  if (permissionsLoading || (canQuery && prescription === undefined)) return <Loading text="Caricamento prescrizione esterna…" />
  if (!canView || !prescription) return <div className="space-y-4"><h1 className="text-2xl font-bold text-gray-900">Prescrizione non disponibile</h1><p className="text-gray-600">La prescrizione non è disponibile nella clinica attiva o non hai il permesso di consultarla.</p><Button asChild variant="outline"><Link to="/prescriptions?lab=external">Torna alle prescrizioni</Link></Button></div>

  return <div className="space-y-6">
    <Link to="/prescriptions?lab=external" aria-disabled={savingEdit || undefined} tabIndex={savingEdit ? -1 : undefined} onClick={(event) => { if (savingEdit) event.preventDefault() }} className="inline-flex items-center gap-2 rounded text-sm text-gray-700 hover:text-gray-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Prescrizioni esterne</Link>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="break-words text-2xl font-bold tracking-tight text-gray-900">{prescription.patient_name}</h1>
        <p className="mt-1 break-words text-gray-600">{prescription.number} · {prescription.laboratory_name}</p>
        <div className="mt-3"><ExternalPrescriptionStatusBadge status={prescription.status} /></div>
      </div>
      {!editing && <div className="flex flex-col gap-2 sm:flex-row">
        {canEdit && <Button variant="outline" disabled={savingStatus} onClick={() => { setEditing(true); setError(null); setNotice(null) }}><Pencil className="mr-2 h-4 w-4" aria-hidden="true" />Modifica</Button>}
        <Button variant="primoup" className="text-gray-950" onClick={download} disabled={downloading}><Download className="mr-2 h-4 w-4" aria-hidden="true" />{downloading ? 'Creazione PDF…' : 'Scarica PDF'}</Button>
      </div>}
    </div>
    {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</p>}
    {editing ? <div className="rounded-lg border bg-white p-4 sm:p-6"><ExternalPrescriptionForm key={prescription._id} laboratoryId={prescription.laboratory_id} prescription={prescription} onSavingChange={setSavingEdit} onCancel={() => setEditing(false)} onSuccess={() => { setEditing(false); setNotice('Prescrizione aggiornata.') }} /></div> : <>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-6 rounded-lg border bg-white p-4 sm:p-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-900">Lavoro richiesto</h2>
            <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">{prescription.work_description}</p>
          </section>
          {prescription.test_phase && <section className="border-t pt-5"><h2 className="font-semibold text-gray-900">Prova o fase</h2><p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{prescription.test_phase}</p></section>}
          {prescription.notes && <section className="border-t pt-5"><h2 className="font-semibold text-gray-900">Note per il laboratorio</h2><p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">{prescription.notes}</p></section>}
          {prescription.lines.length > 0 && <section className="border-t pt-5">
            <h2 className="font-semibold text-gray-900">Lavorazioni</h2>
            <ul className="mt-3 divide-y">
              {prescription.lines.map((line, index) => <li key={index} className="flex flex-col justify-between gap-2 py-3 sm:flex-row">
                <div className="min-w-0"><p className="break-words text-sm font-medium text-gray-900">{line.description}</p><p className="mt-1 text-sm text-gray-600">Quantità: <span className="tabular-nums">{line.quantity}</span>{canViewPrices && line.unit_cost !== undefined ? ` · ${formatCurrency(line.unit_cost)} / unità` : ''}</p></div>
                {canViewPrices && <span className="shrink-0 text-sm tabular-nums text-gray-800">{line.line_total === undefined ? 'Costo non indicato' : formatCurrency(line.line_total)}</span>}
              </li>)}
            </ul>
            {canViewPrices && prescription.total_cost !== undefined && <p className="border-t pt-4 text-right text-sm text-gray-700">Totale costi inseriti <strong className="ml-3 text-base tabular-nums text-gray-900">{formatCurrency(prescription.total_cost)}</strong></p>}
          </section>}
        </div>
        <aside className="min-w-0 space-y-6">
          <section className="rounded-lg border bg-white p-5">
            <h2 className="font-semibold text-gray-900">Riepilogo</h2>
            <dl className="mt-4 space-y-4 text-sm">
              <div><dt className="text-gray-600">Clinica</dt><dd className="mt-1 break-words font-medium text-gray-900">{prescription.company_snapshot.name}</dd></div>
              <div><dt className="text-gray-600">Laboratorio</dt><dd className="mt-1 break-words font-medium text-gray-900">{prescription.laboratory_name}</dd></div>
              <div><dt className="text-gray-600">Medico prescrittore</dt><dd className="mt-1 break-words font-medium text-gray-900">{prescription.doctor_name}</dd></div>
              <div><dt className="text-gray-600">Creata il</dt><dd className="mt-1 font-medium text-gray-900">{formatDate(prescription.created_at)}</dd></div>
              <div><dt className="text-gray-600">Consegna richiesta</dt><dd className="mt-1 font-medium text-gray-900">{prescription.due_date ? formatDate(new Date(`${prescription.due_date}T12:00:00`).getTime()) : 'Non indicata'}</dd></div>
            </dl>
          </section>
          {canEdit && <section className="border-t pt-5">
            <h2 className="font-semibold text-gray-900">Avanzamento del lavoro</h2>
            <p className="mt-1 text-sm text-gray-600">Aggiorna lo stato quando ricevi novità dal laboratorio.</p>
            <label htmlFor="external-prescription-status" className="mb-1.5 mt-4 block text-sm font-medium text-gray-800">Stato</label>
            <select id="external-prescription-status" value={status} onChange={(event) => setStatus(event.target.value as ExternalPrescriptionStatus)} disabled={savingStatus} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50">
              {externalPrescriptionStatuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <Button className="mt-3 w-full" variant="outline" onClick={changeStatus} disabled={savingStatus || status === prescription.status}>{savingStatus ? 'Aggiornamento…' : 'Aggiorna stato'}</Button>
          </section>}
          <p className="text-sm text-gray-600">Scarica il PDF per stamparlo o consegnarlo al laboratorio.</p>
        </aside>
      </div>
    </>}
  </div>
}
